import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import initialState from 'shared/product_tabs/tab_parts/data/initialState';

import { PCB_PARTS_ID } from 'shared/product_tabs/tab_parts/data/BomConstants';
import { TAB_PCB_PARTS } from 'shared/product_tabs/tabs/tabType';

import { QUOTED } from 'shared/product_tabs/tab_parts/data/BomStatus';
import { filterSubstitutes } from 'shared/product_tabs/tab_parts/utils/helpers';
import { buildBomData } from 'shared/product_tabs/tab_parts/utils/builders';
import { changeTab } from 'shared/product_tabs/tabs/tabSlice';
import { clearBom } from 'shared/product_tabs/tab_parts/slice/reducers/clear_bom';
import { parseBomQuoteProgress } from 'shared/product_tabs/tab_parts/slice/reducers/update_bom_quote';
import { replaceBomLineItemPart } from 'shared/product_tabs/tab_parts/slice/reducers/replace_part';
import { sortBomLineItemsByStatus, updateSelectedBoardQty } from 'shared/product_tabs/tab_parts/slice/reducers/basic_updaters';
import { updateBomOnSaveSuccess } from 'shared/product_tabs/tab_parts/slice/reducers/save_bom';
import { updateOnBomImportSuccess } from 'shared/product_tabs/tab_parts/slice/reducers/import_success';
import { updateUserAction } from 'shared/product_tabs/tab_parts/slice/reducers/update_user_action';
import { parseWorkbook } from 'shared/product_tabs/tab_parts/slice/reducers/parse_workbook';
import { calculateBomPrices } from 'shared/product_tabs/tab_parts/slice/reducers/calculateBomPrices';
import { changeExtraParts } from 'shared/product_tabs/tab_parts/slice/reducers/change_extra_parts';
import { changePricebreak } from 'shared/product_tabs/tab_parts/slice/reducers/change_pricebreak';

import { selectDataForBomSave, selectDataForBomValidation } from 'shared/product_tabs/tab_parts/slice/selectors';
import { changeGlobalParameter } from 'shared/product_tabs/tab_project/actions/changeGlobalParameter';
import { alert } from 'shared/product_tabs/alert/alertSlice';

import {
  getBomLineItemSubstitutes,
  saveBomOnStore,
  searchSubstitutes,
} from 'shared/actions/bomQuote';

export const saveBomData = createAsyncThunk(
  'bomService/saveBomData',
  async (id, thunkAPI) => {
    const bom = selectDataForBomSave(thunkAPI.getState());
    return saveBomOnStore(id, { bom: buildBomData(bom), variant_id: bom.currentVariant });
  }
);

export const getSubstitutes = createAsyncThunk(
  'bomService/getSubstitutes',
  async (bom_line_item_id) => getBomLineItemSubstitutes(bom_line_item_id)
);

export const searchPossibleSubstitutes = createAsyncThunk(
  'bomService/searchPossibleSubstitutes',
  async (params) => {
    const { bom_line_item_id, keyword, quote_number } = params;
    return searchSubstitutes(bom_line_item_id, keyword, quote_number);
  }
);

const bomService = createSlice({
  name: 'bomService',
  initialState,
  reducers: {
    requestStart(state) {
      state.view.loading = true;
      state.view.messages = [];
    },
    requestSuccess(state, action) {
      state.view.loading = false;
      state.view.messages.push({ type: 'success', content: action.payload });
    },
    requestFailure(state, action) {
      state.view.loading = false;
      state.view.messages.push({ type: 'error', content: action.payload });
    },
    requestEnd(state) {
      state.view.loading = false;
    },
    openModal(state) {
      state.view.show_modal = true;
    },
    closeModal(state) {
      state.view.show_modal = false;
    },
    addMessage(state, action) {
      const { type, content } = action.payload;
      state.view.messages.push({ type, content });
    },
    clearMessages(state) {
      state.view.messages = [];
    },
    changeBomFile(state, action) {
      state.bom_file = action.payload;
    },
    changeBomName(state, action) {
      state.name = action.payload;
    },
    changeBomRevision(state, action) {
      state.revision = action.payload;
    },
    changeBomHeader(state, action) {
      state.data_for_import.header = action.payload;
    },
    changeBomFirstRow(state, action) {
      state.data_for_import.first_row = action.payload;
    },
    changeBomParameter(state, action) {
      const { index, value } = action.payload;
      state.data_for_import.bom_parameters[index] = value;
    },
    changeRefDesSeparator(state, action) {
      state.data_for_import.ref_des_separator = action.payload;
    },
    changeRefDesRangeSeparator(state, action) {
      state.data_for_import.ref_des_range_separator = action.payload;
    },
    changeBomBoardQty(state, action) {
      const { index, board_qty } = action.payload;
      state.board_qtys[index] = board_qty;
    },
    changeBomSelectedBoardQty(state, action) {
      const selected_board_qty = action.payload;
      updateSelectedBoardQty(state, selected_board_qty);
      sortBomLineItemsByStatus(state);
    },
    changeBomUserAction(state, action) {
      const { index, user_action } = action.payload;
      updateUserAction(state, index, user_action);
      if (state.bom_status === QUOTED) calculateBomPrices(state);
    },
    changeZipFileInputId(state, action) {
      state.view.zip_file_input_id = action.payload;
    },
    changeBomSaved(state, action) {
      state.saved = action.payload;
    },
    updateBomStatus(state, action) {
      state.bom_status = action.payload;
    },
    updateBomBoardQtys(state, action) {
      state.board_qtys = action.payload;
    },
    updateUnquotedBomLineItems(state, action) {
      const { bom_line_items } = action.payload;
      parseBomQuoteProgress(state, bom_line_items);
      sortBomLineItemsByStatus(state);
    },
    updateBomQuoteProgress(state, action) {
      const { step, total_steps, current_pn } = action.payload;
      state.quote_progress.step = step;
      state.quote_progress.total_steps = total_steps;
      state.quote_progress.current_pn = current_pn;
    },
    updateBomPrices(state) {
      if (state.bom_status === QUOTED) calculateBomPrices(state);
    },
    updateFileDirectory(state, action) {
      state.view.directory = action.payload;
    },
    updateShowBomSelection(state, action) {
      state.view.show_bom_selection = action.payload;
    },
    updateBomSelected(state, action) {
      state.view.bom_selected = action.payload;
    },
    updateAvailableBomInZip(state, action) {
      state.view.available_bom_in_zip = action.payload;
    },
    updateUploadZipFile(state, action) {
      state.view.uploaded_zip_file = action.payload;
    },
    updateChangedZipFile(state, action) {
      state.view.changed_zip_file = action.payload;
    },
    changeBomLineItemExtraParts(state, action) {
      changeExtraParts(state, action.payload);
      if (state.bom_status === QUOTED) calculateBomPrices(state);
    },
    changeBomLineItemPricebreak(state, action) {
      changePricebreak(state, action.payload);
      if (state.bom_status === QUOTED) calculateBomPrices(state);
    },
    cleanUpBomData(state, action) {
      const remove_item = action.payload;
      const new_state = clearBom(state, remove_item);
      if (new_state) {
        return { ...new_state };
      }

      return state;
    },
    replaceBomData(state, action) {
      const old_bom_data = action.payload;
      const new_state = { ...old_bom_data };
      return { ...new_state };
    },
    addBomBoardQty(state, action) {
      const { board_qty } = action.payload;
      state.board_qtys.push(board_qty);
    },
    removeLastBomBoardQty(state) {
      state.board_qtys = state.board_qtys.slice(0, -1);
    },
    updateDataForImport(state) {
      parseWorkbook(state);
    },
    replaceBomParameters(state, action) {
      state.data_for_import.bom_parameters = action.payload;
    },
    replaceBomWorkbook(state, action) {
      state.data_for_import.workbook = action.payload;
    },
    replacePart(state, action) {
      const { bom_line_item_id, part } = action.payload;
      replaceBomLineItemPart(state, bom_line_item_id, part);
      sortBomLineItemsByStatus(state);
      if (state.bom_status === QUOTED) calculateBomPrices(state);
    },
    replacePartSupplier(state, action) {
      const { bom_line_item_id, part, part_supplier } = action.payload;
      replaceBomLineItemPart(state, bom_line_item_id, part, part_supplier);
      sortBomLineItemsByStatus(state);
      if (state.bom_status === QUOTED) calculateBomPrices(state);
    },
    importBomSuccess(state, action) {
      const { bom, initial_selected_board_qty } = action.payload;
      updateOnBomImportSuccess(state, bom, initial_selected_board_qty);
    },
    checkBomInitialValues(state, action) {
      const { product_slug, revision } = action.payload;
      if (state.name === '') state.name = `PCBUnlimited-Quote-for-${product_slug}`;
      if (state.revision === '') state.revision = revision;
    },
    replaceSubstitutes(state, action) {
      const { offers } = action.payload;
      state.view.substitutes = filterSubstitutes(offers, state.selected_board_qty);
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getSubstitutes.pending, (state) => {
        state.view.loading = true;
        state.view.messages = [];
      })
      .addCase(getSubstitutes.fulfilled, (state, action) => {
        state.view.loading = false;
        const { result } = action.payload;
        state.view.substitutes = filterSubstitutes(result.offers, state.selected_board_qty);
      })
      .addCase(getSubstitutes.rejected, (state) => {
        state.view.loading = false;
        state.view.messages.push({ type: 'error', content: `Couldn't get Substitutes for Item` });
      })
      .addCase(searchPossibleSubstitutes.pending, (state) => {
        state.view.loading = true;
        state.view.messages = [];
      })
      .addCase(searchPossibleSubstitutes.fulfilled, (state, action) => {
        state.view.loading = false;
        const { result } = action.payload;
        state.view.substitutes = filterSubstitutes(result.offers, state.selected_board_qty);
      })
      .addCase(searchPossibleSubstitutes.rejected, (state) => {
        state.view.loading = false;
        state.view.messages.push({ type: 'error', content: `Couldn't get Substitutes for Item` });
      })
      .addCase(saveBomData.pending, (state) => {
        state.view.loading = true;
      })
      .addCase(saveBomData.fulfilled, (state, action) => {
        const { product_quote_number } = action.payload;
        updateBomOnSaveSuccess(state, product_quote_number);
      })
      .addCase(saveBomData.rejected, (state) => {
        state.view.loading = false;
        state.view.messages.push({ type: 'error', content: `Couldn't save BOM bom, please try again.` });
      })
      .addCase(changeGlobalParameter, (state, action) => {
        const { name, value } = action.payload;
        if (['part_number', 'revision'].includes(name)) {
          const key = name === 'part_number' ? 'name' : 'revision';
          state[key] = value;
        } else if (name === 'zip_input_id') {
          state.view.zip_file_input_id = value;
        } else if (name === 'fabrication_slug') {
          state.view.fabrication_slug = value;
        }
      });
  }
});

export const {
  addBomBoardQty,
  addMessage,
  changeBomFile,
  changeBomName,
  changeBomRevision,
  changeBomBoardQty,
  changeBomSelectedBoardQty,
  changeBomHeader,
  changeBomFirstRow,
  changeBomLineItemExtraParts,
  changeBomLineItemPricebreak,
  changeBomParameter,
  changeBomSaved,
  changeBomUserAction,
  changeRefDesSeparator,
  changeRefDesRangeSeparator,
  changeZipFileInputId,
  checkBomInitialValues,
  cleanUpBomData,
  clearMessages,
  closeModal,
  importBomSuccess,
  openModal,
  removeLastBomBoardQty,
  replaceBomData,
  replaceBomParameters,
  replaceBomWorkbook,
  replacePart,
  replacePartSupplier,
  replaceSubstitutes,
  requestStart,
  requestSuccess,
  requestFailure,
  requestEnd,
  updateAvailableBomInZip,
  updateBomBoardQtys,
  updateBomQuoteProgress,
  updateBomPrices,
  updateBomSelected,
  updateBomStatus,
  updateChangedZipFile,
  updateDataForImport,
  updateFileDirectory,
  updateShowBomSelection,
  updateUnquotedBomLineItems,
  updateUploadZipFile,
} = bomService.actions;

export default bomService.reducer;

export const validate = () => (dispatch, getState) => {
  const {
    bomSaved,
    bomChangedZipFile,
    bomCurrentVariant,
  } = selectDataForBomValidation(getState());

  if (!bomCurrentVariant || bomCurrentVariant !== PCB_PARTS_ID) return true;

  if (bomSaved && bomChangedZipFile) {
    dispatch(changeTab(TAB_PCB_PARTS));
    dispatch(alert('You have uploaded a new zip file, please quote again your bom.'));
    return false;
  }

  return true;
};